import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import DisplayErrorPopup from '../DisplayError'
import { useState } from 'react'
import CustomDialog from '../CustomDialog'
import { RootState } from '../../store'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'
import { Can } from '../../utils/AbilityUtil'
import { AssignDiscipline, AssignStatus } from '../../models/Review'
import { assignEngineerToProject } from '../../services/reviewService'
import { User } from '../../models/Organization'

interface AssignEngineerModalProps {
  isOpen: boolean
  discipline: AssignDiscipline
  handleClose: () => void
}

const AssignEngineerModalComponent: React.FC<AssignEngineerModalProps> = ({
  isOpen,
  discipline,
  handleClose,
}) => {
  const dispatch = useDispatch<any>()
  const [buttonGroup, setbuttonGroup] = useState<string>('Team')
  const users = useSelector((state: RootState) => state.organization.users)
  const usersColors =
    useSelector((state: RootState) => state.organization.userColors) ?? []

  const onAssignClick = (engineer: User) => {
    dispatch(
      assignEngineerToProject({
        discipline: discipline.discipline,
        discipline_status: AssignStatus.AssignRequested,
        status: AssignStatus.NotAssigned,
        organization_id: 9,
        user_id: engineer.id,
        file_id: discipline.file_id,
      }),
    )
  }

  return (
    <Can I={AttributeActionNames.Read} a={AttributeResourceNames.Users}>
      <CustomDialog
        isOpen={isOpen}
        title=''
        hideActions={true}
        handleClose={handleClose}
      >
        <DisplayErrorPopup />
        <Grid
          container
          rowGap={'1rem'}
          sx={{
            maxHeight: '757px',
            boxSizing: 'border-box',
            width: '30rem',
          }}
        >
          <Grid size={12}>
            <Box
              sx={{
                width: '75%',
                display: 'flex',
              }}
            >
              <ToggleButtonGroup
                size='small'
                color='primary'
                value={buttonGroup}
                exclusive
                aria-label='Platform'
                sx={{
                  borderRadius: '4px',
                  '.MuiToggleButton-root': {
                    border: 'none',
                  },
                }}
              >
                {['Team'].map((value, index) => (
                  <ToggleButton
                    key={index}
                    value={value}
                    onClick={() => setbuttonGroup(value)}
                    sx={{
                      width: '5rem',
                      fontSize: '0.64rem',
                      textTransform: 'capitalize',
                      p: '5px',
                      backgroundColor: '#F2F2F2',
                      '&:hover': {
                        backgroundColor: '#E5F0FF',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#1705E5',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#1705E5',
                        },
                      },
                    }}
                  >
                    {value}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid size={12}>
            <Divider flexItem />
          </Grid>
          <Grid size={12}>
            <FormControl sx={{ width: '100%' }}>
              <Grid container rowGap={'10px'}>
                <Grid size={12}>
                  <FormLabel
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      lineHeight: 'normal',
                    }}
                  >
                    Search
                  </FormLabel>
                </Grid>
                <Grid size={12}>
                  <TextField
                    sx={{
                      width: '100%',
                      mt: 0,
                      mb: 0,
                      '& .MuiInputBase-input': {
                        color: '#04002E',
                        fontSize: '0.7rem',
                        lineHeight: 'normal',
                      },
                      '& .MuiOutlinedInput-root': {
                        boxShadow: '0px 3px 10px 0px rgba(187, 187, 187, 0.25)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#C3C3C3',
                        opacity: 1,
                        fontSize: '0.7rem',
                      },
                    }}
                    size='small'
                    placeholder='Search by name or Role'
                    autoFocus
                    margin='dense'
                    id='email'
                    name='email'
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Divider flexItem />
          </Grid>

          <Grid size={12}>
            <Grid
              container
              sx={{
                width: '100%',
              }}
            >
              <Grid size={12}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                    color: '#797979',
                    mb: '0.5rem',
                  }}
                >
                  All Engineers
                </Typography>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  overflowY: 'auto',
                  maxHeight: '500px',
                }}
              >
                {!!users &&
                  users.map((user, index) => (
                    <Grid
                      size={12}
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      p={'0.5rem 1rem'}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#E5F0FF',
                          '& .assign-button': {
                            display: 'block',
                          },
                        },
                      }}
                    >
                      <Box
                        gap={'0.75rem'}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box display={'flex'}>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='32'
                            height='32'
                            viewBox='0 0 32 32'
                            fill='none'
                          >
                            <circle
                              cx='16'
                              cy='16'
                              r='16'
                              fill={
                                usersColors?.find((usr) => usr.id === user.id)
                                  ?.color ?? '#04002E'
                              }
                            />
                            <text
                              x='50%'
                              y='50%'
                              textAnchor='middle'
                              dy='.3em'
                              fontSize='14'
                              fill='#fff'
                              fontWeight='bold'
                            >
                              {user.user_name
                                .split(' ')
                                .map((item) => item.charAt(0))
                                .join('')
                                .toUpperCase()}
                            </text>
                          </svg>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: 'normal',
                            color: '#04002E',
                          }}
                        >
                          {user.user_name}
                        </Typography>
                      </Box>
                      <Button
                        variant='contained'
                        className='assign-button'
                        onClick={() => {
                          onAssignClick(user)
                          handleClose()
                        }}
                        sx={{
                          fontSize: '0.64rem',
                          background: '#FF3B47',
                          textTransform: 'capitalize',
                          minWidth: '5rem',
                          height: '1.5rem',
                          display: 'none',
                          lineHeight: 'normal',
                        }}
                      >
                        Assign to Project
                      </Button>
                    </Grid>
                  ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </CustomDialog>
    </Can>
  )
}

export default AssignEngineerModalComponent
